import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { PickingList } from 'src/entities/pickingListEntity';
import { useQueryPickingList } from 'src/hooks/useQueryPickingList';

import LoadingComponent from './LoadingComponent';

export type PresenterProps = {
  models: PickingList[];
  date: string;
  unit: string;
  barcodeShippers: string[];
}

type Props = {
  shiftIds: number[];
  date: string;
  presenter: FC<PresenterProps>;
  unit: string;
  onCountChange?: (count: number) => void;
  initialSelectedShippers?: string[];
  initialBarcodeShippers?: string[];
}

export function computePickingPageCount(models: PickingList[]): number {
  return models.reduce((total, course) => {
    if (course.dispatches.length === 0) return total + 1;
    const courseCount = course.dispatches.reduce((dispatchTotal, loadingDate) => {
      const dateCount = loadingDate.shippers.reduce(
        (shipperTotal, shipper) => shipperTotal + shipper.unloads.length, 0
      );
      return dispatchTotal + dateCount;
    }, 0);
    return total + courseCount;
  }, 0);
}

export function buildPickingShipperOptions(models: PickingList[]): string[] {
  const names = new Set<string>();
  models.forEach((course) => {
    course.dispatches.forEach((dispatch) => {
      dispatch.shippers.forEach((shipper) => {
        if (shipper.shipper_name) names.add(shipper.shipper_name);
      });
    });
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b, 'ja'));
}

export function filterPickingListsByShippers(models: PickingList[], selectedShippers: string[]): PickingList[] {
  const selected = new Set(selectedShippers);
  return models
    .map((course) => ({
      ...course,
      dispatches: course.dispatches
        .map((dispatch) => ({
          ...dispatch,
          shippers: dispatch.shippers.filter((shipper) => selected.has(shipper.shipper_name)),
        }))
        .filter((dispatch) => dispatch.shippers.length > 0),
    }))
    .filter((course) => course.dispatches.length > 0);
}

const PickingListComponent: FC<Props> = memo(({
  shiftIds,
  date,
  presenter: Presenter,
  unit,
  onCountChange,
  initialSelectedShippers = [],
  initialBarcodeShippers = [],
}) => {
  const pickingQuery = useQueryPickingList(shiftIds);
  const [dialogOpen, setDialogOpen] = useState(initialSelectedShippers.length === 0);
  const [selectedShippers, setSelectedShippers] = useState<string[]>(initialSelectedShippers);
  const [barcodeShippers, setBarcodeShippers] = useState<string[]>(initialBarcodeShippers);

  const shipperOptions = useMemo(
    () => (pickingQuery.data ? buildPickingShipperOptions(pickingQuery.data) : []),
    [pickingQuery.data]
  );
  const filteredPickingLists = useMemo(
    () => (pickingQuery.data ? filterPickingListsByShippers(pickingQuery.data, selectedShippers) : []),
    [pickingQuery.data, selectedShippers]
  );

  const count = computePickingPageCount(filteredPickingLists);

  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  if (pickingQuery.isLoading) {
    return <LoadingComponent />;
  }

  if (pickingQuery.isError) {
    return <>Error</>;
  }

  const toggleShipper = (name: string) => {
    setSelectedShippers((prev) => {
      if (prev.includes(name)) {
        setBarcodeShippers((barcodePrev) => barcodePrev.filter((shipper) => shipper !== name));
        return prev.filter((shipper) => shipper !== name);
      }
      return [...prev, name];
    });
  };

  const toggleBarcodeShipper = (name: string, checked: boolean) => {
    setBarcodeShippers((prev) => (
      checked ? Array.from(new Set([...prev, name])) : prev.filter((shipper) => shipper !== name)
    ));
  };

  return (
    <>
      <Dialog open={dialogOpen} maxWidth="xs" fullWidth>
        <DialogTitle>荷主の選択</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            出力対象の荷主を選択してください（複数選択可）。
          </Typography>
          <Typography display="block" variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            「バーコード」をONにした荷主は、伝票Noをバーコードで出力します。
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflow: 'auto', mt: 2 }}>
            {shipperOptions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedShippers.length === shipperOptions.length}
                      indeterminate={selectedShippers.length > 0 && selectedShippers.length < shipperOptions.length}
                      onChange={(event) => {
                        setSelectedShippers(event.target.checked ? shipperOptions : []);
                        if (!event.target.checked) setBarcodeShippers([]);
                      }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>荷主</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        selectedShippers.length > 0 &&
                        selectedShippers.every((shipper) => barcodeShippers.includes(shipper))
                      }
                      indeterminate={
                        selectedShippers.some((shipper) => barcodeShippers.includes(shipper)) &&
                        !selectedShippers.every((shipper) => barcodeShippers.includes(shipper))
                      }
                      disabled={selectedShippers.length === 0}
                      onChange={(event) => setBarcodeShippers(event.target.checked ? [...selectedShippers] : [])}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>バーコード</Typography>}
                  sx={{ mr: 0 }}
                />
              </Box>
            )}
            <List dense disablePadding>
              {shipperOptions.length === 0 && (
                <ListItem>
                  <Typography variant="caption" color="text.secondary">対象データがありません</Typography>
                </ListItem>
              )}
              {shipperOptions.map((name) => {
                const isSelected = selectedShippers.includes(name);
                return (
                  <ListItem key={name} sx={{ py: 0.25, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={isSelected} onChange={() => toggleShipper(name)} />}
                      label={<Typography variant="body2">{name}</Typography>}
                    />
                    <Checkbox
                      size="small"
                      checked={barcodeShippers.includes(name)}
                      disabled={!isSelected}
                      inputProps={{ 'aria-label': `${name} バーコード` }}
                      onChange={(event) => toggleBarcodeShipper(name, event.target.checked)}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.close()}>戻る</Button>
          <Button variant="contained" onClick={() => setDialogOpen(false)} disabled={selectedShippers.length === 0}>
            出力へ進む
          </Button>
        </DialogActions>
      </Dialog>
      {!dialogOpen && (
        <Presenter
          models={filteredPickingLists}
          date={date}
          unit={unit}
          barcodeShippers={barcodeShippers}
        />
      )}
    </>
  );
});

export default PickingListComponent;
